import { Contract, type ContractList, Membership } from '@kalos/kalos-rpc';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';

import { MEMBERSHIP_STATUS_AWAITING_CONFIRMATION } from '../../modules/Memberships/utils/constants';
import { isContractCurrent } from '../../tools/contractStatus';
import { ContractClientService, MembershipClientService } from '../../tools/helpers';
import { queryKeys } from './constants';
import { type EntityFilter } from './utils';

export type ContractFilter = EntityFilter<Contract>;
export const useContractsBatchGetQuery = <TSelectData = ContractList,>({
  enabled = true,
  filter,
  select,
}: {
  enabled?: boolean;
  filter: ContractFilter;
  select?: (data: ContractList) => TSelectData;
}) => {
  return useQuery({
    queryKey: [
      queryKeys.contracts.root,
      queryKeys.contracts.list,
      queryKeys.contracts.getHash(filter),
    ],
    queryFn: async () => {
      return await ContractClientService.BatchGet(Contract.create(filter));
    },
    enabled,
    select,
  });
};

/** Contract currently in effect for a customer — see `isContractCurrent`. */
export const useCurrentContractQuery = ({
  customerId,
  enabled = true,
}: {
  customerId: number | undefined;
  enabled?: boolean;
}) => {
  return useContractsBatchGetQuery<Contract | null>({
    enabled: enabled && !!customerId,
    filter: { userId: customerId ?? 0, isActive: 1, withoutLimit: true },
    select: (list) => {
      const today = format(new Date(), 'yyyy-MM-dd');
      return list.results.find((c) => isContractCurrent(c, today)) ?? null;
    },
  });
};

/**
 * The customer's contract for the CURRENT period, if any — a renewed-early customer carries two
 * isActive rows (current + future successor), so "existing" means the one whose dates cover today,
 * not the most recent flag.
 */
export const useExistingContract = ({
  customerId,
  enabled = true,
}: {
  customerId: number | undefined;
  enabled?: boolean;
}) => {
  return useContractsBatchGetQuery<Contract | null>({
    enabled: enabled && !!customerId,
    filter: { userId: customerId ?? 0, isActive: 1, withoutLimit: true },
    select: (list) => {
      const today = format(new Date(), 'yyyy-MM-dd');
      return (
        list.results.find(
          (c) =>
            c.isActive === 1 &&
            !!c.dateStarted &&
            !!c.dateEnded &&
            c.dateStarted.slice(0, 10) <= today &&
            c.dateEnded.slice(0, 10) >= today,
        ) ?? null
      );
    },
  });
};

export const useContractQuery = ({
  filter = {},
  enabled = true,
}: {
  filter?: ContractFilter;
  enabled?: boolean;
}) => {
  return useQuery({
    queryKey: [queryKeys.contracts.root, queryKeys.contracts.getHash(filter)],
    enabled,
    queryFn: async () => {
      const req = Contract.create(filter);
      return await ContractClientService.Get(req);
    },
  });
};

/**
 * Contract a job belongs to: the job's own `contractId` when it has one, otherwise the
 * customer's active contract. Both queries are declared unconditionally and gated by `enabled`.
 */
export const useJobContract = ({
  contractId,
  customerId,
  enabled = true,
}: {
  contractId: number;
  customerId: number | undefined;
  enabled?: boolean;
}): { data: Contract | null; isLoading: boolean } => {
  const hasContractId = contractId > 0;
  const jobContractQuery = useContractQuery({
    filter: { id: contractId },
    enabled: enabled && hasContractId,
  });
  const customerContractQuery = useExistingContract({
    customerId,
    enabled: enabled && !hasContractId,
  });
  if (hasContractId) {
    return { data: jobContractQuery.data ?? null, isLoading: jobContractQuery.isLoading };
  }
  return { data: customerContractQuery.data ?? null, isLoading: customerContractQuery.isLoading };
};

/**
 * Flips a soft-deleted contract record back on — a bare isActive=1 fieldmask update, nothing
 * else. The record's dates, billing, and memberships are untouched; derived statuses recompute.
 */
export const useReactivateContractMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      contractId,
      membershipIds = [],
    }: {
      contractId: number;
      /** Cancelled memberships to resurrect: their cancel evidence is cleared alongside. */
      membershipIds?: number[];
    }) => {
      if (contractId <= 0) throw new Error('No contract to reactivate.');
      // The RPC wrapper swallows errors and resolves undefined — a falsy response is the failure.
      const res = await ContractClientService.Update(
        Contract.create({ id: contractId, isActive: 1, fieldMask: ['IsActive'] }),
      );
      if (!res) throw new Error('The server rejected the change.');

      // Clear exactly what buildCancelMembershipReq wrote. pbsql includes masked fields even when
      // empty, and '0001-01-01' is the no-date sentinel both the views and the frontend treat as
      // unset. is_active is left alone: a never-activated row goes back to awaiting confirmation
      // and re-activates through the normal approval flow, not here.
      const failed: number[] = [];
      for (const id of membershipIds.filter((n) => n > 0)) {
        const cleared = await MembershipClientService.Update(
          Membership.create({
            id,
            cancelReason: '',
            cancelTiming: '',
            cancelEffectiveDate: '0001-01-01 00:00:00',
            // The cancelled stamp (status_id=3) is what the dashboard's status ladder falls
            // through to once the fields are clear. status_id carries an FK to the status
            // lookup, so it clears to the born state, not 0.
            statusId: MEMBERSHIP_STATUS_AWAITING_CONFIRMATION,
            fieldMask: ['CancelReason', 'CancelTiming', 'CancelEffectiveDate', 'StatusId'],
          }),
        );
        if (!cleared) failed.push(id);
      }
      if (failed.length > 0) {
        throw new Error(
          `Contract reactivated, but membership ${failed.join(', ')} kept its cancellation — retry.`,
        );
      }
      return res;
    },
    onSettled: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: [queryKeys.contracts.root] }),
        queryClient.invalidateQueries({ queryKey: [queryKeys.memberships.root] }),
      ]),
  });
};

export const useDeleteContractByIdMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: Contract['id']) => {
      const req = Contract.create({ id });
      return ContractClientService.Delete(req);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.contracts.root],
      });
    },
  });
};
